import React, { useEffect, useState } from "react";
import { Menu, User, X, Mail, Phone, Building2 } from "lucide-react";

// Import your actual images
import logo from "./logo.png";
import placeholder from "./placeholder.jpg";


const Header = ({ onGoProfile, onToggleSidebar }) => {
  const [userData, setUserData] = useState({ 
    name: "", 
    profilePhoto: null,
    email: "",
    role: "",
    mobile: "",
    university: ""
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const getUser = () => {
      try {
        const userJson = localStorage.getItem("user");
        if (userJson) {
          const user = JSON.parse(userJson);
          setUserData({
            name: user.name || "Guest User",
            profilePhoto: user.profilePhoto || null,
            email: user.email || "Not provided",
            role: user.role || "User",
            mobile: user.mobile || null,
            university: user.university || null
          });
        }
      } catch (error) {
        console.error("Error parsing user:", error);
      }
    };

    getUser();
    
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleProfileClick = () => setProfileModalOpen(true);
  const closeProfileModal = (e) => {
    if (e) e.stopPropagation();
    setProfileModalOpen(false);
  };

  const getProfileImage = () => {
    if (imageError || !userData.profilePhoto) {
      return placeholder;
    }
    return userData.profilePhoto;
  };

  const ProfileAvatar = ({ size = "md", showBorder = false }) => {
    const sizeClasses = {
      sm: "w-6 h-6 sm:w-7 sm:h-7",
      md: "w-8 h-8",
      lg: "w-32 h-32"
    };

    return (
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden ${showBorder ? 'border-4 border-blue-400 shadow-xl' : ''} bg-gradient-to-br from-blue-600 to-cyan-600`}>
        {!imageError && userData.profilePhoto ? (
          <img 
            src={userData.profilePhoto}
            alt="Profile"
            onError={() => setImageError(true)}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : (
          <img 
            src={placeholder}
            alt="Default Profile"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
        )}
      </div>
    );
  };

  return (
    <>
      <header
        className={`bg-gray-900 backdrop-blur-md shadow-xl sticky top-0 z-50 w-full border-b ${
          isScrolled
            ? "border-gray-800/70 shadow-lg"
            : "border-gray-800/40"
        } transition-all duration-500`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          {/* Left: Logo and Menu */}
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={onGoProfile}
              className="focus:outline-none group flex items-center"
              aria-label="Go to Profile"
            >
              <img
                src={logo}
                alt="UniHuB Logo"
                className="h-7 w-auto sm:h-8 lg:h-9 transition-all duration-300 group-hover:scale-110 drop-shadow-lg filter"
              />
            </button>

            <button
              onClick={() => {
                onToggleSidebar();
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              className="lg:hidden p-2 rounded-full bg-blue-800/50 hover:bg-blue-700/70 transition-all duration-300 transform hover:scale-110 active:scale-95"
              aria-label="Toggle Menu"
            >
              <Menu className={`w-5 h-5 sm:w-6 sm:h-6 text-white transition-transform duration-300 ${mobileMenuOpen ? 'rotate-90' : ''}`} />
            </button>
          </div>

          {/* Mobile: Name and Avatar */}
          <div className="lg:hidden flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            
            <span className="font-medium text-xs sm:text-sm text-blue-100">
              {userData.name.split(" ")[0]}
            </span>
            
            <button 
              onClick={handleProfileClick}
              className="ml-1 p-1 rounded-full backdrop-blur-lg bg-blue-800/50 hover:bg-blue-700/70 transition-all duration-300 transform hover:scale-110 active:scale-95 group"
              aria-label="Open Profile"
            >
              <ProfileAvatar size="sm" />
            </button>
          </div>

          {/* Desktop: Welcome Text and Avatar */}
          <div className="hidden lg:flex items-center gap-3 text-sm lg:text-base text-slate-100">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>
            
            <span className="font-medium tracking-wide text-blue-100">
              Welcome, {userData.name}
            </span>
            
            <button 
              onClick={handleProfileClick}
              className="ml-2 p-1 rounded-full bg-blue-800/50 hover:bg-blue-700/70 transition-all duration-300 transform hover:scale-110 active:scale-95 group"
              aria-label="Open Profile"
            >
              <ProfileAvatar size="md" />
            </button>
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      {profileModalOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={closeProfileModal}
        >
          <div 
            className="bg-gradient-to-br from-gray-800 via-gray-900 to-slate-900 rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-scaleIn border border-blue-600/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <div className="flex justify-end p-3">
              <button 
                onClick={closeProfileModal}
                className="text-gray-300 hover:text-white p-2 rounded-full hover:bg-blue-800/50 transition-all duration-300 transform hover:scale-110 active:scale-95 hover:rotate-90 group"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="px-6 pb-8 pt-2 text-center">
              {/* Profile Image */}
              <div className="mx-auto mb-6 relative group inline-block">
                <ProfileAvatar size="lg" showBorder />
              </div>
              
              {/* User Info */}
              <h3 className="text-2xl font-bold text-white mb-1">{userData.name}</h3>
              <p className="text-blue-300 mb-6 text-sm uppercase tracking-wide">{userData.role}</p>
              
              {/* Details Card */}
              <div className="bg-blue-800/30 rounded-xl p-5 text-left backdrop-blur-sm border border-blue-400/20 space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-blue-300 uppercase tracking-wide mb-1">Email</p>
                    <p className="text-white text-sm break-words">{userData.email}</p>
                  </div>
                </div>
                
                {userData.mobile && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-blue-300 uppercase tracking-wide mb-1">Mobile</p>
                      <p className="text-white text-sm">{userData.mobile}</p>
                    </div>
                  </div>
                )}
                
                {userData.university && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-blue-300 uppercase tracking-wide mb-1">University</p>
                      <p className="text-white text-sm">{userData.university}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </>
  );
};

export default Header;